"use client";

/**
 * Client-side plumbing for every "take this AI summary somewhere" action:
 * clipboard (plain text / markdown), file download (.md / .txt / .pdf), and the
 * iOS/Android share sheet. Audio stays in `use-summary-speech.ts`, which owns
 * Polly playback and caching.
 *
 * One hook rather than per-surface handlers because there are four summary
 * surfaces (company/contact/deal card, inline popover, ticket, task) and each
 * had grown its own copy of the button wiring — which is exactly how the
 * download button ended up labelled "Download" while producing an MP3.
 */

import { useCallback, useState } from "react";
import {
  markdownToPlainText,
  summaryExportFilename,
} from "@/lib/ai/summary-export/format";
import {
  parseSummaryExportError,
  validateSummarySubject,
  type SummaryCopyFormat,
  type SummaryExportErrorCode,
  type SummaryFileFormat,
  type SummarySubject,
} from "@/lib/ai/summary-export/schema";

export type { SummaryCopyFormat, SummaryFileFormat, SummarySubject };

/**
 * What a caller hands in — looser than `SummarySubject` on purpose: a panel may
 * hold null/empty for a record still loading or a summary still streaming. The
 * schema is what turns that into a validated subject or a refusal.
 */
export interface SummaryExportSubject {
  title: string;
  markdown: string;
  subject?: string | null;
  sourceUrl?: string | null;
}

export interface UseSummaryExportResult {
  /** Which action is in flight, for per-item spinners. */
  busy: SummaryFileFormat | null;
  error: string | null;
  /** Machine-readable failure, so a UI can pick WHICH remedy to offer. */
  errorCode: SummaryExportErrorCode | null;
  /** Transient confirmation: which copy format was last copied. */
  copied: SummaryCopyFormat | null;
  copy: (format: SummaryCopyFormat) => Promise<boolean>;
  downloadFile: (format: SummaryFileFormat) => Promise<boolean>;
  /** True when the platform has a native share sheet (iOS/iPadOS, Android). */
  canShare: boolean;
  share: () => Promise<boolean>;
}

/** Copy text to the clipboard, falling back for older/insecure contexts. */
async function writeClipboard(value: string): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      // Permission denied or a non-secure context — fall through.
    }
  }
  if (typeof document === "undefined") return false;
  // execCommand is deprecated but is still the only fallback that works in a
  // WKWebView without clipboard permission, which is where iPad users live.
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  textarea.remove();
  return ok;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke on the next tick so Safari has actually started the download.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function useSummaryExport(getSubject: () => SummaryExportSubject | null): UseSummaryExportResult {
  const [busy, setBusy] = useState<SummaryFileFormat | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<SummaryExportErrorCode | null>(null);
  const [copied, setCopied] = useState<SummaryCopyFormat | null>(null);

  const fail = useCallback((code: SummaryExportErrorCode, message: string): false => {
    setErrorCode(code);
    setError(message);
    return false;
  }, []);

  /**
   * Validate against the SAME schema the route uses, before spending a round
   * trip: a summary mid-stream or past the size cap is refused here by the same
   * rule, with the same wording, instead of coming back as a 400 to interpret.
   */
  const readSubject = useCallback(
    (): { ok: true; subject: SummarySubject } | { ok: false } => {
      const raw = getSubject();
      if (!raw) return { ok: false };
      const validated = validateSummarySubject({
        title: raw.title,
        markdown: raw.markdown,
        ...(raw.subject ? { subject: raw.subject } : {}),
        ...(raw.sourceUrl ? { sourceUrl: raw.sourceUrl } : {}),
      });
      if (!validated.ok) {
        fail(validated.code, validated.message);
        return { ok: false };
      }
      return { ok: true, subject: validated.subject };
    },
    [getSubject, fail]
  );

  const copy = useCallback(
    async (format: SummaryCopyFormat): Promise<boolean> => {
      setError(null);
      setErrorCode(null);
      const read = readSubject();
      if (!read.ok) return false;
      const value =
        format === "markdown"
          ? read.subject.markdown
          : markdownToPlainText(read.subject.markdown);
      const ok = await writeClipboard(value);
      if (!ok) {
        // Not a server code: a blocked clipboard is a browser-permission problem
        // and its remedy is different, so it gets its own message.
        setError("Couldn't copy to the clipboard. Select the summary text and copy manually.");
        return false;
      }
      setCopied(format);
      window.setTimeout(() => setCopied(null), 2000);
      return true;
    },
    [readSubject]
  );

  const downloadFile = useCallback(
    async (format: SummaryFileFormat): Promise<boolean> => {
      setError(null);
      setErrorCode(null);
      const read = readSubject();
      if (!read.ok) return false;
      const { subject } = read;
      setBusy(format);
      try {
        const response = await fetch("/api/ai/summary/export", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // Shape is summaryExportRequestSchema — the object the route parses.
          body: JSON.stringify({ ...subject, format }),
        });
        if (!response.ok) {
          // Map the CODE, never the server's prose: the text a user reads is ours,
          // and it names the remedy (copy instead / another format / reload).
          const { code, message } = parseSummaryExportError(
            response.status,
            await response.json().catch(() => null)
          );
          return fail(code, message);
        }
        triggerBlobDownload(
          await response.blob(),
          summaryExportFilename(subject.title, format, subject.subject)
        );
        return true;
      } catch {
        // Network/offline. Never surface a raw exception string to a user.
        return fail(
          "SUMMARY_EXPORT_RENDER_FAILED",
          "Couldn't reach Hub to build that file. Check your connection and try again."
        );
      } finally {
        setBusy(null);
      }
    },
    [readSubject, fail]
  );

  const canShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  const share = useCallback(async (): Promise<boolean> => {
    if (typeof navigator === "undefined" || typeof navigator.share !== "function") return false;
    setError(null);
    setErrorCode(null);
    const read = readSubject();
    if (!read.ok) return false;
    const { subject } = read;
    try {
      // Plain text, not markdown: the share sheet's destinations are Messages,
      // Mail and Notes, none of which render markdown.
      await navigator.share({
        title: subject.title,
        text: markdownToPlainText(subject.markdown),
        ...(subject.sourceUrl ? { url: subject.sourceUrl } : {}),
      });
      return true;
    } catch (err) {
      // A user dismissing the sheet throws AbortError — not an error to report.
      if (err instanceof DOMException && err.name === "AbortError") return false;
      setError("Couldn't open the share sheet. Copy the summary instead.");
      return false;
    }
  }, [readSubject]);

  return { busy, error, errorCode, copied, copy, downloadFile, canShare, share };
}
